<template>
  <!-- User Dropdown -->
  <div class="dropdown d-inline-block ms-2">
    <!-- User Dropdown Toggle -->
    <button
      type="button"
      class="btn btn-alt-secondary d-flex align-items-center"
      id="page-header-user-dropdown"
      data-bs-toggle="dropdown"
      aria-haspopup="true"
      aria-expanded="false"
      style="height: 38px"
    >
      <div class="d-flex align-items-center">
        <!-- Avatar with initials -->
        <div class="avatar bg-primary-darker rounded-circle">
          <span class="avatar-text">{{ userInitials }}</span>
        </div>
        <span class="d-none d-sm-inline-block ms-2 fw-semibold">{{
          user?.name
        }}</span>
        <i class="fa fa-fw fa-angle-down opacity-50 ms-1"></i>
      </div>
    </button>

    <div
      class="dropdown-menu dropdown-menu-end dropdown-menu-lg p-0 border-0 fs-sm"
      aria-labelledby="page-header-user-dropdown"
      @click.stop
    >
      <!-- User Header -->
      <div class="p-3 bg-body-light rounded-top">
        <div class="d-flex align-items-center">
          <div class="avatar bg-primary-darker rounded-circle me-3">
            <span class="avatar-text">{{ userInitials }}</span>
          </div>
          <div>
            <div class="fw-semibold">{{ user?.name }}</div>
            <div
              class="fs-sm text-muted email-copy"
              @click="copyEmailToClipboard"
              title="Click to copy email"
            >
              <span>{{ user?.email }}</span>
              <i class="fa fa-copy ms-1 copy-icon"></i>
            </div>
          </div>
        </div>
      </div>

      <!-- User Links -->
      <div class="p-2">
        <router-link
          to="/profile"
          class="dropdown-item d-flex align-items-center"
        >
          <i class="fa fa-fw fa-user me-2"></i>
          <span>Profile</span>
        </router-link>
        <router-link
          to="/billing"
          class="dropdown-item d-flex align-items-center"
        >
          <i class="fa fa-fw fa-credit-card me-2"></i>
          <span>Billing</span>
        </router-link>

        <div role="separator" class="dropdown-divider"></div>

        <button @click="logOut" class="dropdown-item d-flex align-items-center">
          <i class="fa fa-fw fa-sign-out-alt me-2"></i>
          <span>Log Out</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { computed, onMounted } from 'vue'
  import { useAuth0 } from '@auth0/auth0-vue'
  import { useAlert } from '@/composables/useAlert'
  import { useTemplateStore } from '@/stores/template'

  const { user, logout } = useAuth0()
  const alertService = useAlert()
  const templateStore = useTemplateStore()

  // Get user initials for avatar
  const userInitials = computed(() => {
    if (!user.value?.name) return 'PS'
    return user.value.name
      .split(' ')
      .map(part => part.charAt(0))
      .join('')
      .substring(0, 2)
      .toUpperCase()
  })

  // Logout function
  const logOut = () => {
    // Limpar o cache de token antes de fazer logout
    templateStore.clearTokenCache()

    // Limpar outros dados específicos do login
    localStorage.removeItem('publinker_login_target_site')
    localStorage.removeItem('publinker_login_target_path')
    localStorage.removeItem('publinker_login_timestamp')
    localStorage.removeItem('publinker_login_original_url')

    // Prosseguir com o logout normal
    logout({
      logoutParams: {
        returnTo: window.location.origin
      }
    })
  }

  // Function to copy email to clipboard
  const copyEmailToClipboard = () => {
    if (user.value?.email) {
      navigator.clipboard
        .writeText(user.value.email)
        .then(() => {
          alertService.showToast('Email copied to clipboard', 'success')
        })
        .catch(err => {
          console.error('Could not copy email: ', err)
          alertService.showToast('Failed to copy email', 'error')
        })
    }
  }

  // Prevent dropdown from closing when clicking inside
  onMounted(() => {
    const dropdownMenu = document.querySelector('.dropdown-menu-lg')
    if (dropdownMenu) {
      dropdownMenu.addEventListener('click', event => {
        // Only prevent propagation if not clicking on a link or button
        if (!event.target.closest('a') && !event.target.closest('button')) {
          event.stopPropagation()
        }
      })
    }
  })
</script>

<style scoped>
  .avatar {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
  }

  .dropdown-menu-lg .avatar {
    width: 36px;
    height: 36px;
  }

  .avatar-text {
    color: white;
    font-weight: bold;
    font-size: 0.75rem;
  }

  .dropdown-menu-lg .avatar-text {
    font-size: 0.9rem;
  }

  /* Email copy styling */
  .email-copy {
    cursor: pointer;
    display: flex;
    align-items: center;
    transition: color 0.15s ease;
  }

  .email-copy:hover {
    color: var(--bs-primary) !important;
  }

  .copy-icon {
    font-size: 0.75rem;
    opacity: 0.5;
  }

  .email-copy:hover .copy-icon {
    opacity: 1;
  }

  /* Dropdown enhancements */
  .dropdown-menu-lg {
    min-width: 300px;
    box-shadow: 0 0.5rem 2rem rgba(0, 0, 0, 0.15);
  }

  @media (max-width: 576px) {
    .dropdown-menu-lg {
      min-width: 260px;
    }
  }
</style>
